import { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as fplColor, typography } from '@figma/fpl-tokens/tokens.stylex';
import { Tabs, useManagedTabs } from '@figma/fpl-components';
import * as stylex from '@stylexjs/stylex';
import { Box } from '@/UI/Box/Box';
import { FlexCenVer } from '@/UI/styles';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AppToggleButtons } from '@/UI/AppToggleButtons/AppToggleButtons';
import { ExternalArrowIcon } from '@/UI/Icons';
import { PlayCircleIcon } from '@/UI/Icons/PlayCircleIcon';
import { FF_WORKFLOW_COMMUNITY_PUBLISHING } from '@/consts/featureFlags';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { color } from '../../colors';
import { I18N_KEYS } from '../../language/keys';
import { Arrow } from './ArrowScroll';
import EducationGalleryCard from './EducationGallery/EducationGalleryCard';
import type { DashboardRecipe } from '@/types/recipes';

type ShowcaseRecipe = DashboardRecipe & {
  showcaseOrder?: number;
};

type Tutorial = {
  id: string;
  poster: string;
  icon: React.ReactNode;
  url: string;
  name: string;
  order: number;
};

const tutorialPlayIcon = <PlayCircleIcon width={36} height={36} />;

const tutorials: Tutorial[] = [
  {
    id: '00',
    poster: 'https://media.weavy.ai/image/upload/v1748958081/product/assets/tutorials_posters/Intro_s89joq.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=YGx90x8XaHI?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.INTRO,
    order: 0,
  },
  {
    id: '10',
    poster:
      'https://media.weavy.ai/image/upload/v1751201353/product/assets/tutorials_posters/gr5rtbqs7jevqrku1rep.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=wMTqdtTexe4?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.WEAVE_101,
    order: 10,
  },
  {
    id: '20',
    poster: 'https://media.weavy.ai/image/upload/v1748958081/product/assets/tutorials_posters/Tutorial_1_pd4ykx.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=ihOFi5lpQr8?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.FIRST_WORKFLOW,
    order: 20,
  },
  {
    id: '30',
    poster: 'https://media.weavy.ai/image/upload/v1748958081/product/assets/tutorials_posters/Tutorial_2_ou1f3v.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=zQGutL3RZbU?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.COMPOSITOR,
    order: 30,
  },
  {
    id: '40',
    poster: 'https://media.weavy.ai/image/upload/v1748958081/product/assets/tutorials_posters/Tutorial_3_tm7uah.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=9uTXPdZbidc?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.THREE_D_MODELS,
    order: 40,
  },
  {
    id: '50',
    poster: 'https://media.weavy.ai/image/upload/v1748958079/product/assets/tutorials_posters/Tutorial_4_bhmk8b.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=aEoHwSdWWWM?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.WORKFLOW_TO_TOOL,
    order: 50,
  },
  {
    id: '60',
    poster:
      'https://media.weavy.ai/image/upload/v1748958081/product/assets/tutorials_posters/Export_import_jkcsbz.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=UxOsCyHmCEM?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.EXPORT_IMPORT,
    order: 60,
  },
  {
    id: '70',
    poster:
      'https://media.weavy.ai/image/upload/v1748958080/product/assets/tutorials_posters/Master_generative_models_h15xsd.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=ykniduApncM?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.GENERATIVE_MODELS,
    order: 70,
  },
  {
    id: '75',
    poster: 'https://media.weavy.ai/image/upload/v1765116365/product/assets/tutorials_posters/Iterators_czqsxi.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=_uIxc7IaW9c?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.ITERATORS,
    order: 75,
  },
  {
    id: '80',
    poster:
      'https://media.weavy.ai/image/upload/v1748958080/product/assets/tutorials_posters/Import_LoRa_model_kgieyn.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=r_Yzt2jqyzs?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.IMPORT_LORA,
    order: 80,
  },
  {
    id: '90',
    poster:
      'https://media.weavy.ai/image/upload/v1748958080/product/assets/tutorials_posters/Mastering_text_prompts_pkwxuy.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=OXyPhupm2bU?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.TEXT_PROMPTS,
    order: 90,
  },
  {
    id: '100',
    poster:
      'https://media.weavy.ai/image/upload/v1748958079/product/assets/tutorials_posters/Bring_external_models_jyx0x8.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=tBIjC5BUGg0?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.EXTERNAL_MODELS,
    order: 100,
  },
  {
    id: '110',
    poster: 'https://media.weavy.ai/image/upload/v1748958079/product/assets/tutorials_posters/Controlnet_rbc8ko.avif',
    icon: tutorialPlayIcon,
    url: 'https://www.youtube.com/watch?v=mWRnijpv2pw?utm_source=figma_weave_gallery',
    name: I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIAL_NAMES.CONTROLNET,
    order: 110,
  },
];

interface ShowCaseRecipesListProps {
  recipes: ShowcaseRecipe[];
}

const enum EducationGalleryTabs {
  WORKFLOWS = 'workflows',
  TUTORIALS = 'tutorials',
}

type ManagedEducationGalleryTabs = ReturnType<typeof useManagedTabs<EducationGalleryTabs>>;

const styles = stylex.create({
  tabsOnSecondaryBackground: {
    '--color-bg': 'transparent',
    '--color-bg-hover': fplColor.bgSecondaryHover,
    '--color-bg-secondary': fplColor.bgTertiary,
  },
});

function EducationGalleryTabSelector({
  value,
  onChange,
  tabProps,
  tabManager,
}: {
  value: EducationGalleryTabs;
  onChange: (tab: EducationGalleryTabs) => void;
  tabProps: ManagedEducationGalleryTabs[0];
  tabManager: ManagedEducationGalleryTabs[2];
}) {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled) {
    return (
      <div {...stylex.props(styles.tabsOnSecondaryBackground)}>
        <Tabs.TabStrip manager={tabManager}>
          <Tabs.Tab {...tabProps[EducationGalleryTabs.WORKFLOWS]}>
            {t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.WORKFLOWS)}
          </Tabs.Tab>
          <Tabs.Tab {...tabProps[EducationGalleryTabs.TUTORIALS]}>
            {t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIALS)}
          </Tabs.Tab>
        </Tabs.TabStrip>
      </div>
    );
  }

  return (
    <AppToggleButtons
      value={value}
      options={[
        {
          value: EducationGalleryTabs.WORKFLOWS,
          label: t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.WORKFLOWS),
          'aria-label': t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.WORKFLOWS),
        },
        {
          value: EducationGalleryTabs.TUTORIALS,
          label: t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIALS),
          'aria-label': t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.TUTORIALS),
        },
      ]}
      onChange={(newTab) => {
        if (newTab !== null) {
          onChange(newTab);
        }
      }}
      aria-label={t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.ARIA)}
    />
  );
}

function EducationGallery({ recipes }: ShowCaseRecipesListProps) {
  const [showLeftScrollArrow, setShowLeftScrollArrow] = useState(false);
  const [showRightScrollArrow, setShowRightScrollArrow] = useState(false);
  const [selectedTab, setSelectedTab] = useState(EducationGalleryTabs.WORKFLOWS);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { value: isCommunityPublishingEnabled } = useFeatureFlag(FF_WORKFLOW_COMMUNITY_PUBLISHING);
  const handleTabChange = useCallback((newTab: EducationGalleryTabs) => {
    setSelectedTab(newTab);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, []);
  const [tabProps, tabPanelProps, tabManager] = useManagedTabs<EducationGalleryTabs>(
    {
      [EducationGalleryTabs.WORKFLOWS]: true,
      [EducationGalleryTabs.TUTORIALS]: true,
    },
    selectedTab,
    handleTabChange,
  );

  useEffect(() => {
    const handleScroll = () => updateScrollArrows();

    const handleWheel = (event: WheelEvent) => {
      if (scrollContainerRef.current) {
        if (event.deltaX !== 0) {
          event.preventDefault();
          scrollContainerRef.current.scrollLeft += event.deltaX;
        }
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      scrollContainer.addEventListener('wheel', handleWheel);
      setTimeout(updateScrollArrows, 100);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
        scrollContainer.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  const goToRecipe = (recipeId: string) => {
    track('Clicked on example workflow', {
      context: 'education_carousel',
      workflow: recipes.find((recipe) => recipe.id === recipeId)?.name,
    });
    const url = `/flow/${recipeId}`;
    window.open(url, '_blank');
  };

  const goToTutorial = (tutorialId: string) => {
    track('Clicked on video tutorial', {
      context: 'education_gallery',
      tutorial: tutorials.find((tutorial) => tutorial.id === tutorialId)?.url,
    });
    const url = tutorials.find((tutorial) => tutorial.id === tutorialId)?.url;
    window.open(url, '_blank');
  };

  const updateScrollArrows = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
      const isScrollable = scrollWidth > clientWidth;
      const isAtStart = scrollLeft <= 1; // Tolerance for minor discrepancies
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setShowLeftScrollArrow(isScrollable && !isAtStart);
      setShowRightScrollArrow(isScrollable && !isAtEnd);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 1000, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -1000, behavior: 'smooth' });
    }
  };

  const handleMouseEnter = () => {
    updateScrollArrows();
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.relatedTarget instanceof Node && !e.currentTarget.contains(e.relatedTarget)) {
      setShowLeftScrollArrow(false);
      setShowRightScrollArrow(false);
    }
  };

  const getGalleryData = (
    selectedTab: EducationGalleryTabs,
    recipes: ShowcaseRecipe[],
    tutorials: Tutorial[],
  ): Array<ShowcaseRecipe | Tutorial> => {
    if (selectedTab === EducationGalleryTabs.WORKFLOWS && recipes) {
      return recipes
        .filter((recipe): recipe is ShowcaseRecipe & { showcaseOrder: number } => recipe.showcaseOrder !== undefined)
        .sort((a, b) => a.showcaseOrder - b.showcaseOrder);
    } else if (selectedTab === EducationGalleryTabs.TUTORIALS && tutorials) {
      return [...tutorials]
        .sort((a, b) => a.order - b.order)
        .map((tutorial) => ({ ...tutorial, name: t(tutorial.name) }));
    }
    return [];
  };

  const galleryContent = (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={scrollContainerRef}
        sx={{
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          mt: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Box sx={{ display: 'inline-flex', gap: 2, px: 3 }}>
          {getGalleryData(selectedTab, recipes, tutorials).map((item) => (
            <EducationGalleryCard
              key={item.id}
              cardContent={item}
              handleClick={selectedTab === EducationGalleryTabs.WORKFLOWS ? goToRecipe : goToTutorial}
              showGradient={true}
            />
          ))}
        </Box>
      </Box>
      {showLeftScrollArrow && <Arrow right={false} onClick={scrollLeft} />}
      {showRightScrollArrow && <Arrow right={true} onClick={scrollRight} />}
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        backgroundColor: isFplMigrationEnabled ? cssVars.color.bgSecondary : color.Black92,
        borderRadius: 2,
        py: 2,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <FlexCenVer sx={{ justifyContent: 'space-between', gap: 2, px: 2 }}>
        <EducationGalleryTabSelector
          value={selectedTab}
          onChange={handleTabChange}
          tabProps={tabProps}
          tabManager={tabManager}
        />
        {selectedTab === EducationGalleryTabs.WORKFLOWS && isCommunityPublishingEnabled === true ? (
          <Box
            component="a"
            href={EXTERNAL_LINKS.figmaCommunity}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: isFplMigrationEnabled ? cssVars.color.text : color.White100,
              fontSize: typography.textBodyMediumFontSize,
              fontWeight: 500,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              textDecorationThickness: '1px',
              whiteSpace: 'nowrap',
              '&:hover': {
                color: isFplMigrationEnabled ? cssVars.color.text : color.White100,
                textDecoration: 'underline',
              },
            }}
          >
            {t(I18N_KEYS.MAIN_DASHBOARD.EDUCATION_GALLERY.COMMUNITY)}
            <ExternalArrowIcon width={16} height={16} />
          </Box>
        ) : null}
      </FlexCenVer>
      {isFplMigrationEnabled ? (
        <Tabs.TabPanel {...tabPanelProps[selectedTab]} width="fill">
          {galleryContent}
        </Tabs.TabPanel>
      ) : (
        galleryContent
      )}
    </Box>
  );
}

export default EducationGallery;
